// src/pages/DemosPage.js
// Interactive Studios / Demos Gallery Page

import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence, useReducedMotion, useScroll, useSpring } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useNavigate } from 'react-router-dom';
import {
  Globe, FileSearch, LineChart, Activity,
  X, ExternalLink, ChevronRight, Check,
  Grid3X3, List, LayoutGrid, Sparkles, Loader2, Star
} from 'lucide-react';
import Navbar from '../components/Navbar';
import SEO from '../components/SEO';
import { domainFilters, statusFilters } from '../data/studiosData';
import { useStudios } from '../hooks/useStudios';
import './DemosPage.css';

// Icon mapping
const iconMap = {
  Globe,
  FileSearch,
  LineChart,
  Activity
};

// Status colors
const statusColors = {
  'live': '#10B981',
  'in-lab': '#F59E0B',
  'coming-soon': '#6B7280'
};

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.08, delayChildren: 0.1 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
  }
};


// Particle Background Component
const ParticleBackground = ({ particleCount = 40 }) => {
  const canvasRef = useRef(null);
  const prefersReducedMotion = useReducedMotion();
  const animationRef = useRef(null);
  const particlesRef = useRef([]);

  const createParticle = useCallback((width, height) => ({
    x: Math.random() * width,
    y: Math.random() * height,
    vx: (Math.random() - 0.5) * 0.15,
    vy: (Math.random() - 0.5) * 0.1,
    size: Math.random() * 2 + 1,
    opacity: Math.random() * 0.3 + 0.1,
    color: Math.random() > 0.5 ? '#00D9FF' : '#8B5CF6'
  }), []);

  useEffect(() => {
    if (prefersReducedMotion) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    let width = canvas.offsetWidth;
    let height = canvas.offsetHeight;

    const handleResize = () => {
      width = canvas.offsetWidth;
      height = canvas.offsetHeight;
      canvas.width = width * window.devicePixelRatio;
      canvas.height = height * window.devicePixelRatio;
      ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    particlesRef.current = Array.from({ length: particleCount }, () =>
      createParticle(width, height)
    );

    const animate = () => {
      ctx.clearRect(0, 0, width, height);

      particlesRef.current.forEach((p) => {
        p.x += p.vx;
        p.y += p.vy;

        if (p.x < 0) p.x = width;
        if (p.x > width) p.x = 0;
        if (p.y < 0) p.y = height;
        if (p.y > height) p.y = 0;

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = p.color;
        ctx.globalAlpha = p.opacity;
        ctx.fill();
        ctx.globalAlpha = 1;
      });

      // Draw connections
      particlesRef.current.forEach((p1, i) => {
        particlesRef.current.slice(i + 1).forEach((p2) => {
          const dx = p1.x - p2.x;
          const dy = p1.y - p2.y;
          const dist = Math.sqrt(dx * dx + dy * dy);

          if (dist < 120) {
            ctx.beginPath();
            ctx.moveTo(p1.x, p1.y);
            ctx.lineTo(p2.x, p2.y);
            ctx.strokeStyle = '#8B5CF6';
            ctx.globalAlpha = 0.08 * (1 - dist / 120);
            ctx.lineWidth = 0.5;
            ctx.stroke();
            ctx.globalAlpha = 1;
          }
        });
      });

      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', handleResize);
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [prefersReducedMotion, particleCount, createParticle]);

  if (prefersReducedMotion) return null;

  return <canvas ref={canvasRef} className="demos-page__particles" />;
};

// Hero Bento Tile Component
const HeroTile = ({ studio, size = 'sm', onClick }) => {
  const Icon = iconMap[studio.icon] || Globe;
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setMousePos({
      x: (e.clientX - rect.left - rect.width / 2) / rect.width,
      y: (e.clientY - rect.top - rect.height / 2) / rect.height
    });
  };

  return (
    <motion.div
      className={`hero-tile hero-tile--${size}`}
      style={{ '--accent': studio.accent }}
      whileHover={{ scale: 1.03, y: -4 }}
      whileTap={{ scale: 0.98 }}
      onClick={() => onClick(studio)}
      onMouseMove={handleMouseMove}
      onMouseLeave={() => setMousePos({ x: 0, y: 0 })}
    >
      <div className="hero-tile__frame">
        {studio.heroScreenshot || studio.thumbnail ? (
          <motion.img
            src={studio.heroScreenshot || studio.thumbnail}
            alt={studio.name}
            className="hero-tile__screenshot"
            style={{
              transform: `translate(${mousePos.x * -4}px, ${mousePos.y * -4}px)`
            }}
          />
        ) : (
          <div className="hero-tile__placeholder">
            <Icon size={size === 'xl' ? 48 : 32} />
          </div>
        )}
      </div>
      <div className="hero-tile__info">
        <div className="hero-tile__badges">
          <span
            className={`hero-tile__status hero-tile__status--${studio.status}`}
            style={{ '--status-color': statusColors[studio.status] }}
          >
            {studio.status === 'live' && <span className="hero-tile__status-dot" />}
            {studio.statusLabel}
          </span>
          {studio.featured && (
            <span className="hero-tile__featured-badge">
              <Star size={10} />
              Featured
            </span>
          )}
        </div>
        <span className="hero-tile__name">{studio.name}</span>
      </div>
    </motion.div>
  );
};

// Filter Chip Component
const FilterChip = ({ label, active, onClick, color, count }) => (
  <motion.button
    className={`filter-chip ${active ? 'filter-chip--active' : ''}`}
    style={active && color ? { '--chip-color': color } : {}}
    onClick={onClick}
    whileHover={{ scale: 1.02 }}
    whileTap={{ scale: 0.97 }}
  >
    {label}
    {count !== undefined && (
      <span className="filter-chip__count">{count}</span>
    )}
  </motion.button>
);

// View Toggle Component
const ViewToggle = ({ view, setView }) => (
  <div className="view-toggle">
    <motion.button
      className={`view-toggle__btn ${view === 'bento' ? 'view-toggle__btn--active' : ''}`}
      onClick={() => setView('bento')}
      whileTap={{ scale: 0.95 }}
      title="Bento layout with featured cards"
    >
      <Grid3X3 size={14} />
    </motion.button>
    <motion.button
      className={`view-toggle__btn ${view === 'grid' ? 'view-toggle__btn--active' : ''}`}
      onClick={() => setView('grid')}
      whileTap={{ scale: 0.95 }}
      title="Grid layout"
    >
      <LayoutGrid size={14} />
    </motion.button>
    <motion.button
      className={`view-toggle__btn ${view === 'list' ? 'view-toggle__btn--active' : ''}`}
      onClick={() => setView('list')}
      whileTap={{ scale: 0.95 }}
      title="List layout"
    >
      <List size={14} />
    </motion.button>
  </div>
);

// Studio Card Component (for Bento and Grid views)
const StudioCard = ({ studio, featured, onClick, index, viewMode }) => {
  const Icon = iconMap[studio.icon] || Globe;
  const isGridMode = viewMode === 'grid';

  return (
    <motion.article
      className={`studio-card ${featured ? 'studio-card--featured' : ''} ${isGridMode ? 'studio-card--grid' : ''}`}
      style={{ '--accent': studio.accent }}
      initial={false}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      layout
      whileHover={{ y: -4, scale: 1.01 }}
      whileTap={{ scale: 0.99 }}
      onClick={() => onClick(studio)}
    >
      <div className="studio-card__header">
        <span
          className={`studio-card__status studio-card__status--${studio.status}`}
          style={{ '--status-color': statusColors[studio.status] }}
        >
          {studio.status === 'live' && <span className="studio-card__status-dot" />}
          {studio.statusLabel}
        </span>
        <span className="studio-card__domain">{studio.domainLabel}</span>
        {studio.featured && (
          <span className="studio-card__featured-badge">
            <Star size={10} />
            Featured
          </span>
        )}
      </div>

      <div className="studio-card__screenshot-wrap">
        <div className="studio-card__browser-frame">
          <div className="studio-card__browser-dots">
            <span /><span /><span />
          </div>
        </div>
        <div className="studio-card__screen">
          {studio.heroScreenshot || studio.thumbnail ? (
            <img
              src={studio.heroScreenshot || studio.thumbnail}
              alt={studio.name}
              className="studio-card__image"
            />
          ) : (
            <div className="studio-card__placeholder">
              <Icon size={isGridMode ? 28 : (featured ? 40 : 32)} />
            </div>
          )}
        </div>
      </div>

      <div className="studio-card__content">
        <h3 className="studio-card__title">{studio.name}</h3>
        <p className="studio-card__subtitle">{studio.shortDescription}</p>
      </div>

      <div className="studio-card__footer">
        <div className="studio-card__tech">
          {studio.techStack.slice(0, isGridMode ? 2 : 3).map((tech, i) => (
            <span key={i} className="studio-card__tech-chip">{tech}</span>
          ))}
        </div>
        <span className="studio-card__link">
          <ChevronRight size={14} />
        </span>
      </div>
    </motion.article>
  );
};

// List Card Component (horizontal row layout)
const ListCard = ({ studio, onClick }) => {
  const Icon = iconMap[studio.icon] || Globe;

  return (
    <motion.article
      className="list-card"
      style={{ '--accent': studio.accent }}
      initial={false}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.2 }}
      layout
      whileHover={{ x: 4, backgroundColor: 'rgba(255, 255, 255, 0.03)' }}
      whileTap={{ scale: 0.995 }}
      onClick={() => onClick(studio)}
    >
      {/* Thumbnail / Icon */}
      <div className="list-card__thumb">
        {studio.heroScreenshot || studio.thumbnail ? (
          <img src={studio.heroScreenshot || studio.thumbnail} alt={studio.name} />
        ) : (
          <div className="list-card__icon">
            <Icon size={24} />
          </div>
        )}
      </div>

      {/* Main content */}
      <div className="list-card__content">
        <div className="list-card__top">
          <h3 className="list-card__title">{studio.name}</h3>
          {studio.featured && (
            <span className="list-card__featured-badge">
              <Star size={10} />
              Featured
            </span>
          )}
          <span
            className={`list-card__status list-card__status--${studio.status}`}
            style={{ '--status-color': statusColors[studio.status] }}
          >
            {studio.status === 'live' && <span className="list-card__status-dot" />}
            {studio.statusLabel}
          </span>
        </div>
        <p className="list-card__desc">{studio.shortDescription}</p>
        <div className="list-card__meta">
          <span className="list-card__domain">{studio.domainLabel}</span>
          <div className="list-card__tech">
            {studio.techStack.slice(0, 3).map((tech, i) => (
              <span key={i}>{tech}</span>
            ))}
          </div>
        </div>
      </div>

      {/* Arrow */}
      <div className="list-card__arrow">
        <ChevronRight size={18} />
      </div>
    </motion.article>
  );
};

// Studio Modal Component - Two-column browser layout
const StudioModal = ({ studio, isOpen, onClose }) => {
  const Icon = studio ? (iconMap[studio.icon] || Globe) : Globe;

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') onClose();
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEsc);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  if (!studio) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="studio-modal__overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={onClose}
        >
          <motion.div
            className="studio-modal"
            style={{ '--accent': studio.accent }}
            initial={{ opacity: 0, scale: 0.96, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Compact Header */}
            <div className="studio-modal__header">
              <h2 className="studio-modal__title">{studio.name}</h2>
              <div className="studio-modal__header-right">
                {studio.featured && (
                  <span className="studio-modal__featured-badge">
                    <Star size={12} />
                    Featured
                  </span>
                )}
                <span
                  className={`studio-modal__status studio-modal__status--${studio.status}`}
                  style={{ '--status-color': statusColors[studio.status] }}
                >
                  {studio.status === 'live' && <span className="studio-modal__status-dot" />}
                  {studio.statusLabel}
                </span>
                <button className="studio-modal__close" onClick={onClose}>
                  <X size={20} />
                </button>
              </div>
            </div>

            {/* Two-column body */}
            <div className="studio-modal__body">
              {/* Left: Browser preview */}
              <div className="studio-modal__preview">
                <div className="studio-modal__browser-chrome">
                  <div className="studio-modal__browser-dots">
                    <span /><span /><span />
                  </div>
                  {studio.demoUrl && (
                    <span className="studio-modal__browser-url">
                      {studio.demoUrl.replace(/^https?:\/\//, '')}
                    </span>
                  )}
                </div>
                <div className="studio-modal__screen">
                  {studio.heroScreenshot || studio.thumbnail ? (
                    <img
                      src={studio.heroScreenshot || studio.thumbnail}
                      alt={studio.name}
                      className="studio-modal__image"
                    />
                  ) : (
                    <div className="studio-modal__placeholder">
                      <Icon size={64} />
                    </div>
                  )}
                </div>
              </div>

              {/* Right: Content */}
              <div className="studio-modal__content">
                <span className="studio-modal__domain">{studio.domainLabel}</span>
                <p className="studio-modal__description">{studio.longDescription}</p>

                <div className="studio-modal__capabilities">
                  <h4>Key Capabilities</h4>
                  <ul>
                    {studio.bullets.map((bullet, i) => (
                      <motion.li
                        key={i}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.3 + i * 0.05 }}
                      >
                        <Check size={16} style={{ color: studio.accent }} />
                        {bullet}
                      </motion.li>
                    ))}
                  </ul>
                </div>

                <div className="studio-modal__tech">
                  <h4>Tech Stack</h4>
                  <div className="studio-modal__tech-chips">
                    {studio.techStack.map((tech, i) => (
                      <span key={i} className="studio-modal__tech-chip">{tech}</span>
                    ))}
                  </div>
                </div>

                <div className="studio-modal__tags">
                  {studio.tags.map((tag, i) => (
                    <span key={i}>{tag}</span>
                  ))}
                </div>

                {/* CTA buttons inside content area */}
                <div className="studio-modal__cta">
                  {studio.demoUrl ? (
                    <a
                      href={studio.demoUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="studio-modal__btn studio-modal__btn--primary"
                    >
                      Open Live Demo <ExternalLink size={16} />
                    </a>
                  ) : (
                    <span className="studio-modal__btn studio-modal__btn--disabled">
                      Demo Coming Soon
                    </span>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Main DemosPage Component
const DemosPage = () => {
  const navigate = useNavigate();

  // Fetch studios from Contentful
  const { studios, loading, error } = useStudios();

  const [activeDomain, setActiveDomain] = useState('all');
  const [activeStatuses, setActiveStatuses] = useState([]);
  const [view, setView] = useState('bento');
  const [selectedStudio, setSelectedStudio] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSticky, setIsSticky] = useState(false);

  const heroRef = useRef(null);
  const controlsRef = useRef(null);

  // Handle "Get in Touch" navigation with delay scroll
  const handleContactClick = useCallback((e) => {
    e.preventDefault();
    navigate('/');
    // Delay to allow page to render before scrolling
    setTimeout(() => {
      const contactSection = document.getElementById('contact');
      if (contactSection) {
        contactSection.scrollIntoView({ behavior: 'smooth' });
      }
    }, 300);
  }, [navigate]);

  const [heroInViewRef, heroInView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  // Scroll progress for progress bar
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001
  });

  // Filter studios
  const filteredStudios = useMemo(() => {
    return studios
      .filter(s => activeDomain === 'all' || s.domain === activeDomain)
      .filter(s => activeStatuses.length === 0 || activeStatuses.includes(s.status))
      .sort((a, b) => (a.order ?? 99) - (b.order ?? 99));
  }, [studios, activeDomain, activeStatuses]);

  // Get featured studios for hero
  const featuredStudios = useMemo(() => {
    const live = studios.filter(s => s.status === 'live');
    const others = studios.filter(s => s.status !== 'live').slice(0, 3);
    return [...live, ...others].slice(0, 4);
  }, [studios]);

  // Status counts
  const statusCounts = useMemo(() => ({
    live: studios.filter(s => s.status === 'live').length,
    'in-lab': studios.filter(s => s.status === 'in-lab').length,
    'coming-soon': studios.filter(s => s.status === 'coming-soon').length
  }), [studios]);

  // Sticky detection
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => setIsSticky(!entry.isIntersecting),
      { threshold: 0, rootMargin: '-80px 0px 0px 0px' }
    );

    if (heroRef.current) observer.observe(heroRef.current);
    return () => observer.disconnect();
  }, []);

  // Toggle status filter
  const toggleStatus = (status) => {
    setActiveStatuses(prev =>
      prev.includes(status)
        ? prev.filter(s => s !== status)
        : [...prev, status]
    );
  };

  // Open modal
  const openModal = (studio) => {
    setSelectedStudio(studio);
    setIsModalOpen(true);
  };

  // Close modal
  const closeModal = () => {
    setIsModalOpen(false);
    setTimeout(() => setSelectedStudio(null), 300);
  };

  // Show loading state
  if (loading) {
    return (
      <div className="demos-page">
        <Navbar />
        <div className="demos-page__loading">
          <Loader2 className="demos-page__spinner" size={32} />
          <p>Loading studios...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="demos-page">
        <Navbar />
        <div className="demos-page__error">
          <p>Unable to load studios. Please try again later.</p>
          <button onClick={() => window.location.reload()}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="demos-page">
      {/* SEO */}
      <SEO
        title="Live Demos"
        description="Explore interactive AI demos and product studios from Rosalytics. See our GenAI copilots, manufacturing analytics, and enterprise solutions in action."
        canonical="/demos"
      />

      {/* Progress bar */}
      <motion.div className="demos-page__progress" style={{ scaleX }} />

      <Navbar />

      {/* Hero Section */}
      <section className="demos-hero" ref={heroRef}>
        <ParticleBackground particleCount={40} />
        <div className="demos-hero__glow demos-hero__glow--cyan" />
        <div className="demos-hero__glow demos-hero__glow--violet" />
        <div className="demos-hero__grid" />

        <div className="demos-hero__container" ref={heroInViewRef}>
          <motion.div
            className="demos-hero__content"
            initial="hidden"
            animate={heroInView ? "visible" : "hidden"}
            variants={containerVariants}
          >
            <motion.div className="demos-hero__badge" variants={itemVariants}>
              <Sparkles size={14} />
              Interactive Studios
            </motion.div>

            <motion.h1 className="demos-hero__title" variants={itemVariants}>
              Explore Live Product{' '}
              <span className="demos-hero__title-gradient">Demos</span>
            </motion.h1>

            <motion.p className="demos-hero__subtitle" variants={itemVariants}>
              Real prototypes solving real industrial problems — from supply chain
              control towers to GenAI copilots and factory intelligence.
            </motion.p>

            <motion.div className="demos-hero__stats" variants={itemVariants}>
              <div className="demos-hero__stat">
                <span className="demos-hero__stat-dot" style={{ background: statusColors['live'] }} />
                <span className="demos-hero__stat-count">{statusCounts['live']}</span>
                <span className="demos-hero__stat-label">Live</span>
              </div>
              <div className="demos-hero__stat">
                <span className="demos-hero__stat-dot" style={{ background: statusColors['in-lab'] }} />
                <span className="demos-hero__stat-count">{statusCounts['in-lab']}</span>
                <span className="demos-hero__stat-label">In Lab</span>
              </div>
              <div className="demos-hero__stat">
                <span className="demos-hero__stat-dot" style={{ background: statusColors['coming-soon'] }} />
                <span className="demos-hero__stat-count">{statusCounts['coming-soon']}</span>
                <span className="demos-hero__stat-label">Coming Soon</span>
              </div>
            </motion.div>

            <motion.p className="demos-hero__micro" variants={itemVariants}>
              Browse studios by domain, status, or tech stack.
            </motion.p>
          </motion.div>

          <motion.div
            className="demos-hero__mosaic"
            initial="hidden"
            animate={heroInView ? "visible" : "hidden"}
            variants={containerVariants}
          >
            {featuredStudios[0] && (
              <motion.div variants={itemVariants} className="demos-hero__mosaic-xl">
                <HeroTile
                  studio={featuredStudios[0]}
                  size="xl"
                  onClick={openModal}
                />
              </motion.div>
            )}
            <div className="demos-hero__mosaic-sm">
              {featuredStudios.slice(1, 4).map((studio, i) => (
                <motion.div key={studio.id} variants={itemVariants}>
                  <HeroTile
                    studio={studio}
                    size="sm"
                    onClick={openModal}
                  />
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Filter Controls */}
      <div
        ref={controlsRef}
        className={`demos-controls ${isSticky ? 'demos-controls--sticky' : ''}`}
      >
        <div className="demos-controls__container">
          <div className="demos-controls__filters">
            <div className="demos-controls__group">
              {domainFilters.map(filter => (
                <FilterChip
                  key={filter.id}
                  label={filter.label}
                  active={activeDomain === filter.id}
                  onClick={() => setActiveDomain(filter.id)}
                />
              ))}
            </div>

            <div className="demos-controls__divider" />

            <div className="demos-controls__group">
              {statusFilters.filter(f => f.id !== 'all').map(filter => (
                <FilterChip
                  key={filter.id}
                  label={filter.label}
                  active={activeStatuses.includes(filter.id)}
                  onClick={() => toggleStatus(filter.id)}
                  color={statusColors[filter.id]}
                  count={statusCounts[filter.id]}
                />
              ))}
            </div>
          </div>

          <div className="demos-controls__actions">
            <span className="demos-controls__count">
              Showing {filteredStudios.length} of {studios.length} studios
            </span>
            <ViewToggle view={view} setView={setView} />
          </div>
        </div>
      </div>

      {/* Main Grid */}
      <section className="demos-grid-section">
        <div className="demos-grid-section__container">
          <motion.div
            className={`demos-grid demos-grid--${view}`}
            layout
          >
            {view === 'list' ? (
              // List view - horizontal rows
              filteredStudios.map((studio) => (
                <ListCard
                  key={studio.id}
                  studio={studio}
                  onClick={openModal}
                />
              ))
            ) : (
              // Bento or Grid view - cards
              filteredStudios.map((studio, index) => (
                <StudioCard
                  key={studio.id}
                  studio={studio}
                  featured={view === 'bento' && studio.featured}
                  onClick={openModal}
                  index={index}
                  viewMode={view}
                />
              ))
            )}
          </motion.div>

          {filteredStudios.length === 0 && (
            <motion.div
              className="demos-grid__empty"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <p>No studios match your filters.</p>
              <button onClick={() => { setActiveDomain('all'); setActiveStatuses([]); }}>
                Clear filters
              </button>
            </motion.div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="demos-footer">
        <div className="demos-footer__container">
          <p>
            Want to see a custom demo for your industry?{' '}
            <a href="/#contact" onClick={handleContactClick}>Get in touch</a>
          </p>
        </div>
      </footer>

      {/* Modal */}
      <StudioModal
        studio={selectedStudio}
        isOpen={isModalOpen}
        onClose={closeModal}
      />
    </div>
  );
};

export default DemosPage;
